import "./easter-eggs.css";

/* ─── shared helpers ───────────────────────────────────────── */
type ParticleMode = "fall" | "rise";

function burst(opts: { chars: string[]; count: number; duration: number; mode: ParticleMode; color?: string; size?: [number, number] }) {
    const { chars, count, duration, mode, color, size = [14, 28] } = opts;
    const layer = document.createElement("div");
    layer.className = "egg-layer";

    for (let i = 0; i < count; i++) {
        const p = document.createElement("span");
        p.className = "egg-particle";
        p.textContent = chars[Math.floor(Math.random() * chars.length)];
        p.style.left = `${Math.random() * 100}vw`;
        p.style.fontSize = `${size[0] + Math.random() * (size[1] - size[0])}px`;
        if (color) p.style.color = color;
        p.style.setProperty("--dx", `${(Math.random() - 0.5) * 160}px`);
        p.style.animation = `${mode === "fall" ? "eggFall" : "eggRise"} ${duration * (0.6 + Math.random() * 0.4)}ms linear ${Math.random() * duration * 0.5}ms both`;
        layer.appendChild(p);
    }

    document.body.appendChild(layer);
    setTimeout(() => layer.remove(), duration * 1.6 + 300);
}

function timedClass(el: HTMLElement, className: string, ms: number) {
    if (el.classList.contains(className)) return;
    el.classList.add(className);
    setTimeout(() => el.classList.remove(className), ms);
}

/* ─── Harry Potter ─────────────────────────────────────────── */

/** "nox" — the screen goes dark, only a wand light follows the cursor. "lumos" turns it back on. */
let noxEl: HTMLDivElement | null = null;
let noxMove: ((e: PointerEvent) => void) | null = null;

export function triggerNox() {
    if (noxEl) return;

    noxEl = document.createElement("div");
    noxEl.className = "egg-nox";
    noxEl.style.setProperty("--x", `${window.innerWidth / 2}px`);
    noxEl.style.setProperty("--y", `${window.innerHeight / 2}px`);
    document.body.appendChild(noxEl);
    requestAnimationFrame(() => noxEl?.classList.add("egg-nox-in"));

    noxMove = (e: PointerEvent) => {
        noxEl?.style.setProperty("--x", `${e.clientX}px`);
        noxEl?.style.setProperty("--y", `${e.clientY}px`);
    };
    window.addEventListener("pointermove", noxMove);
}

export function triggerLumos() {
    if (!noxEl) return;
    const el = noxEl;
    noxEl = null;

    if (noxMove) window.removeEventListener("pointermove", noxMove);
    noxMove = null;

    el.classList.remove("egg-nox-in");
    setTimeout(() => el.remove(), 600);
    burst({ chars: ["✦", "✧", "✨"], count: 24, duration: 1800, mode: "rise", color: "#ffd45e", size: [12, 22] });
}

/** "wingardium leviosa" — everything on screen floats up and gently wobbles, then lands again. */
let levitating = false;

export function triggerLeviosa() {
    if (levitating) return;

    const targets = Array.from(
        document.querySelectorAll<HTMLElement>(".content-screen .card-post, .content-screen .row-user, .row-fillter-search, .container-search")
    );
    if (!targets.length) return;

    levitating = true;
    targets.forEach(el => {
        el.style.setProperty("--lev-y", `${-(24 + Math.random() * 40)}px`);
        el.style.setProperty("--lev-r", `${((Math.random() - 0.5) * 6).toFixed(2)}deg`);
        el.style.animation = `eggLevitate ${5 + Math.random() * 1.5}s ease-in-out ${Math.random() * 0.5}s both`;
    });

    setTimeout(() => {
        targets.forEach(el => {
            el.style.animation = "";
            el.style.removeProperty("--lev-y");
            el.style.removeProperty("--lev-r");
        });
        levitating = false;
    }, 7200);
}

/** "expecto patronum" — a silvery-blue glow with sparks rising across the screen. */
export function triggerPatronus() {
    const glow = document.createElement("div");
    glow.className = "egg-patronus";
    document.body.appendChild(glow);
    setTimeout(() => glow.remove(), 4200);

    burst({ chars: ["✦", "✧", "•", "✺"], count: 60, duration: 3200, mode: "rise", color: "#bfe6ff", size: [10, 26] });
}

/** "avada kedavra" — a green flash. */
export function triggerAvadaKedavra() {
    const flash = document.createElement("div");
    flash.className = "egg-avada";
    document.body.appendChild(flash);
    setTimeout(() => flash.remove(), 1300);
}

/* ─── Other themes ─────────────────────────────────────────── */

/** "matrix" — digital rain over the page for a few seconds. */
export function triggerMatrix() {
    if (document.getElementById("egg-matrix")) return;

    const canvas = document.createElement("canvas");
    canvas.id = "egg-matrix";
    canvas.className = "egg-matrix";
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    document.body.appendChild(canvas);
    requestAnimationFrame(() => canvas.classList.add("egg-matrix-in"));

    const ctx = canvas.getContext("2d");
    if (!ctx) return canvas.remove();

    const size = 16;
    const glyphs = "アイウエオカキクケコサシスセソタチツテト0123456789";
    const drops = Array.from({ length: Math.floor(canvas.width / size) }, () => Math.random() * -40);
    ctx.font = `${size}px monospace`;

    const timer = setInterval(() => {
        ctx.fillStyle = "rgba(0, 0, 0, 0.08)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = "#00ff66";

        drops.forEach((y, i) => {
            ctx.fillText(glyphs[Math.floor(Math.random() * glyphs.length)], i * size, y * size);
            drops[i] = y * size > canvas.height && Math.random() > 0.975 ? 0 : y + 1;
        });
    }, 50);

    setTimeout(() => {
        canvas.classList.remove("egg-matrix-in");
        setTimeout(() => {
            clearInterval(timer);
            canvas.remove();
        }, 700);
    }, 6000);
}

/** "let it snow" */
export function triggerSnow() {
    burst({ chars: ["❄", "❅", "❆", "•"], count: 90, duration: 7000, mode: "fall", color: "#ffffff", size: [10, 26] });
}

/** "party" — confetti + rainbow hue spin. */
export function triggerParty() {
    burst({ chars: ["🎉", "🎊", "✨", "🎈", "⭐"], count: 70, duration: 4500, mode: "fall", size: [16, 32] });
    timedClass(document.documentElement, "egg-disco", 5000);
}

/** "upside down" (Stranger Things) — toggles a flipped, reddish world. */
export function toggleUpsideDown() {
    document.documentElement.classList.toggle("egg-upside-down");
}

/** Clears every persistent effect (nox, askew, upside down). Call it when leaving the search screen. */
export function resetEasterEggs() {
    triggerLumosSilently();
    document.documentElement.classList.remove("askew", "egg-upside-down", "egg-disco", "barrel-roll");
    document.getElementById("egg-matrix")?.remove();
    document.querySelectorAll(".egg-layer, .egg-patronus, .egg-avada").forEach(el => el.remove());
}

function triggerLumosSilently() {
    if (!noxEl) return;
    if (noxMove) window.removeEventListener("pointermove", noxMove);
    noxEl.remove();
    noxEl = null;
    noxMove = null;
}
